import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  ExportOutlined,
  SettingOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  SyncOutlined,
  MoreOutlined,
  CodeOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  HddOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { servicesService, ordersService } from '../../services';
import { ServiceEvent, ServiceEventResponse } from '../../services/services.service';
import { VMService } from '../../types/service.type';
import { Order } from '../../types/order.type';
import { ServiceStatusBadge, ConfirmationModal, ServiceCreatingScreen, ServiceEventsList, DetailPageHeader, HeaderActionButton, HeaderActionMenu } from '../../components/shared';
import { getServiceAge } from '../../utils/serviceHelpers';
import { useNotificationContext } from '../../contexts/NotificationContext';
import MonitoringTab from '../ServiceDetail/components/MonitoringTab';
import BackupsTab from '../../components/shared/BackupsTab';
import LazyServiceEventsList from '../../components/shared/LazyServiceEventsList';

const VM_EC2_SPECS: Record<string, { vcpu: number; memoryMB: number }> = {
  't3.nano':    { vcpu: 2, memoryMB: 512 },
  't3.micro':   { vcpu: 2, memoryMB: 1024 },
  't3.small':   { vcpu: 2, memoryMB: 2048 },
  't3.medium':  { vcpu: 2, memoryMB: 4096 },
  't3.large':   { vcpu: 2, memoryMB: 8192 },
  't3.xlarge':  { vcpu: 4, memoryMB: 16384 },
  't3.2xlarge': { vcpu: 8, memoryMB: 32768 },
};

function formatVMMemory(mb: number): string {
  return mb >= 1024 ? `${mb / 1024} GB` : `${mb} MB`;
}

const VM_REGION_NAMES: Record<string, string> = {
  'us-east-1':       'US East (N. Virginia)',
  'us-west-2':       'US West (Oregon)',
  'eu-west-1':       'EU (Irlanda)',
  'ap-southeast-1':  'Asia Pacífico (Singapur)',
};

/**
 * Mapea el level a un tipo de evento para la UI
 */
const mapLevelToEventType = (level?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!level) return 'info';
  
  const levelUpper = level.toUpperCase();
  switch (levelUpper) {
    case 'SUCCESS':
      return 'success';
    case 'ERROR':
      return 'error';
    case 'WARNING':
      return 'warning';
    case 'INFO':
    default:
      return 'info';
  }
};

/**
 * Mapea el stackStatus a un tipo de evento para la UI (fallback si no hay level)
 */
const mapStackStatusToEventType = (stackStatus?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!stackStatus) return 'info';
  
  const status = stackStatus.toUpperCase();
  if (status.includes('COMPLETE') && !status.includes('FAILED')) {
    return 'success';
  }
  if (status.includes('FAILED') || status.includes('ERROR') || status.includes('ROLLBACK')) {
    return 'error';
  }
  if (status.includes('IN_PROGRESS') || status.includes('PROGRESS')) {
    return 'info';
  }
  return 'info';
};

/**
 * Normaliza el mensaje del evento, manejando estados especiales como "stacking" y "creating"
 */
const normalizeEventMessage = (message: string, stackStatus?: string): string => {
  if (!message) return 'No message';
  
  // Normalizar estados "stacking" y "creating" a un mensaje más claro
  const messageLower = message.toLowerCase();
  const statusLower = stackStatus?.toLowerCase() || '';
  
  // Si el mensaje o status contiene "stacking", mostrar como "Creando"
  if (messageLower.includes('stacking') || statusLower.includes('stacking')) {
    return message.replace(/stacking/gi, 'Creating').trim();
  }
  
  // Si contiene "creating", asegurar que esté en español si es necesario
  if (messageLower.includes('creating')) {
    return message.replace(/creating/gi, 'Creating').trim();
  }
  
  return message;
};

/**
 * Convierte eventos del API al formato de la UI
 */
const mapServiceEventsToUI = (events: ServiceEventResponse[]): ServiceEvent[] => {
  return events.map((event, index) => {
    // Convertir createdAt a Date de forma segura
    let timestamp: Date;
    try {
      // Verificar que createdAt exista y sea válido
      if (!event.createdAt || typeof event.createdAt !== 'string') {
        console.warn(`Evento ${index} sin createdAt válido:`, event);
        timestamp = new Date(); // Usar fecha actual como fallback
      } else {
        timestamp = new Date(event.createdAt);
        // Validar que la fecha sea válida
        if (isNaN(timestamp.getTime())) {
          console.warn(`Fecha inválida en evento ${index}:`, event.createdAt);
          timestamp = new Date(); // Usar fecha actual como fallback
        }
      }
    } catch (error) {
      console.warn(`Error al parsear fecha en evento ${index}:`, error);
      timestamp = new Date(); // Usar fecha actual como fallback
    }

    // Usar level si está disponible, sino usar stackStatus como fallback
    const eventType = event.level 
      ? mapLevelToEventType(event.level)
      : mapStackStatusToEventType(event.stackStatus);

    // Normalizar el mensaje, manejando estados especiales
    const message = normalizeEventMessage(event.message || 'No message', event.stackStatus);

    // Detectar si es un evento de Kubiy
    const isKubiyEvent = event.source?.toLowerCase() === 'kubiy';
    
    // Detectar si es un evento de eliminación (mejorado para no depender solo de Kubiy)
    const messageLower = message.toLowerCase();
    const stackStatusLower = event.stackStatus?.toLowerCase() || '';
    const levelUpper = event.level?.toUpperCase() || '';
    
    // Criterios para identificar evento de eliminación:
    // 1. Mensaje contiene palabras clave de eliminación
    const hasDeletionKeywords = messageLower.includes('deleted') || 
                                 messageLower.includes('eliminado') ||
                                 messageLower.includes('service deleted') ||
                                 messageLower.includes('servicio eliminado');
    
    // 2. StackStatus indica eliminación completada
    const hasDeleteCompleteStatus = stackStatusLower.includes('delete_complete') ||
                                     stackStatusLower.includes('delete_completed') ||
                                     stackStatusLower.includes('deleted');
    
    // 3. Level es SUCCESS y el mensaje sugiere eliminación (evento final exitoso)
    const isSuccessfulDeletion = levelUpper === 'SUCCESS' && hasDeletionKeywords;
    
    // Es un evento de eliminación si:
    // - Es de Kubiy Y tiene palabras clave de eliminación (comportamiento original)
    // - O tiene stackStatus de DELETE_COMPLETE
    // - O es SUCCESS con palabras clave de eliminación (evento final)
    const isDeletionEvent = (isKubiyEvent && hasDeletionKeywords) ||
                            hasDeleteCompleteStatus ||
                            isSuccessfulDeletion;

    // Construir detalles: solo agregar información adicional si NO hay description NI message del API
    let details: string | undefined = undefined;
    
    // Si hay description del API, usarla directamente
    if (event.description) {
      details = event.description;
    } 
    // Si hay message del API (y no description), no agregar detalles adicionales
    else if (event.message && event.message.trim() !== '') {
      // No agregar detalles adicionales si ya hay un message
      details = undefined;
    }
    // Solo construir detalles si no hay ni description ni message
    else {
      const detailsParts: string[] = [];
      
      // Agregar phase si está disponible
      if (event.phase) {
        detailsParts.push(`Phase: ${event.phase}`);
      }
      
      // Agregar statusReason si está disponible
      if (event.statusReason) {
        detailsParts.push(event.statusReason);
      }
      
      // Agregar información del recurso si está disponible
      if (event.resourceType) {
        detailsParts.push(`Resource: ${event.resourceType}`);
      }
      
      // Si no hay detalles pero hay detailType, usarlo como fallback
      if (detailsParts.length === 0 && event.detailType) {
        detailsParts.push(event.detailType);
      }
      
      details = detailsParts.length > 0 ? detailsParts.join(' • ') : undefined;
    }

    return {
      id: event.SK || `event-${index}-${event.createdAt}`,
      timestamp,
      type: eventType,
      message,
      details,
      source: event.source,
      isKubiyEvent,
      isDeletionEvent,
    };
  });
};

// Interfaz para reglas de seguridad en formato tabla
interface SecurityRule {
  id: string;
  direction: 'ingress' | 'egress';
  type: 'RDP' | 'MSSQL' | 'MySQL' | 'PostgreSQL' | 'SSH' | 'HTTP' | 'HTTPS' | 'Custom';
  fromPort: number;
  toPort: number;
  protocol: 'tcp' | 'udp' | 'icmp';
  cidr: string;
  description: string;
}

// Mapeo inverso: de puerto/protocolo a tipo
const getTypeFromPort = (fromPort: number, toPort: number, protocol: string): SecurityRule['type'] => {
  if (fromPort === 22 && toPort === 22 && protocol === 'tcp') return 'SSH';
  if (fromPort === 3389 && toPort === 3389 && protocol === 'tcp') return 'RDP';
  if (fromPort === 1433 && toPort === 1433 && protocol === 'tcp') return 'MSSQL';
  if (fromPort === 3306 && toPort === 3306 && protocol === 'tcp') return 'MySQL';
  if (fromPort === 5432 && toPort === 5432 && protocol === 'tcp') return 'PostgreSQL';
  if (fromPort === 80 && toPort === 80 && protocol === 'tcp') return 'HTTP';
  if (fromPort === 443 && toPort === 443 && protocol === 'tcp') return 'HTTPS';
  return 'Custom';
};

// Mapear reglas de seguridad del API al formato de la UI
const mapSecurityRulesFromAPI = (
  securityRules: any,
  direction: 'ingress' | 'egress'
): SecurityRule[] => {
  if (!securityRules || !securityRules[direction] || !Array.isArray(securityRules[direction])) {
    return [];
  }

  return securityRules[direction].map((rule: any, index: number) => {
    const fromPort = Math.round(rule.fromPort || 0);
    const toPort = Math.round(rule.toPort || 0);
    const protocol = (rule.protocol || 'tcp').toLowerCase() as 'tcp' | 'udp' | 'icmp';
    
    return {
      id: `rule-${direction}-${index}-${Date.now()}`,
      direction,
      type: getTypeFromPort(fromPort, toPort, protocol),
      fromPort,
      toPort,
      protocol,
      cidr: rule.cidr || '',
      description: rule.description || '',
    };
  });
};

// Función helper para obtener la imagen del sistema operativo
const getOSImage = (product: any, serviceOs?: string): string | null => {
  // Obtener el nombre del OS de diferentes fuentes
  const osName = (product?.os || product?.name || serviceOs || '').toLowerCase();
  
  // Verificar si coincide con alguno de los sistemas operativos conocidos
  if (osName.includes('red hat') || osName.includes('redhat') || osName.includes('rhel')) {
    return '/images/linux.png'; // Red Hat usa linux genérico
  }
  if (osName.includes('suse') || osName.includes('sles') || osName.includes('opensuse')) {
    return '/images/suse.png';
  }
  if (osName.includes('debian')) {
    return '/images/debian.png';
  }
  if (osName.includes('ubuntu')) {
    return '/images/ubuntu.png';
  }
  if (osName.includes('amazon') || osName.includes('amzn')) {
    return '/images/amazon linux.png';
  }
  if (osName.includes('windows')) {
    return '/images/windows.png';
  }
  if (osName.includes('linux')) {
    return '/images/linux.png';
  }
  
  // Si no coincide, usar la imagen del producto si está disponible
  if (product?.images && product.images.length > 0) {
    return product.images[0];
  }
  
  return null;
};

const VMDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { success, error: showError, info } = useNotificationContext();
  
  const [service, setService] = useState<VMService | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [product, setProduct] = useState<any>(null); // Información del producto del API
  const [vmConfig, setVmConfig] = useState<any>(null); // Configuración de VM desde metadata.vmConfig
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<ServiceEvent[]>([]);
  const [isRefreshingEvents, setIsRefreshingEvents] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'events' | 'security' | 'documentation' | 'monitoring' | 'backups'>('events');
  // Estado para reglas de seguridad en formato tabla (separadas por dirección)
  const [ingressRules, setIngressRules] = useState<SecurityRule[]>([]);
  const [egressRules, setEgressRules] = useState<SecurityRule[]>([]);
  // Estado para guardar las reglas originales del servidor (para comparar cambios)
  const [originalIngressRules, setOriginalIngressRules] = useState<SecurityRule[]>([]);
  const [originalEgressRules, setOriginalEgressRules] = useState<SecurityRule[]>([]);
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<SecurityRule | null>(null);
  const [newRule, setNewRule] = useState<Omit<SecurityRule, 'id'>>({
    direction: 'ingress',
    type: 'SSH',
    fromPort: 22,
    toPort: 22,
    protocol: 'tcp',
    cidr: '',
    description: '',
  });
  const [isSavingIngress, setIsSavingIngress] = useState(false);
  const [isSavingEgress, setIsSavingEgress] = useState(false);
  const [securityTab, setSecurityTab] = useState<'ingress' | 'egress'>('ingress');
  const [isDownloadingCredentials, setIsDownloadingCredentials] = useState(false);
  const [isDownloadingSshKey, setIsDownloadingSshKey] = useState(false);
  const hasFetchedRef = useRef<string | null>(null); // Guardar el id que ya se procesó
  const abortControllerRef = useRef<AbortController | null>(null);

  // Obtener el orderId de la URL o del state
  const orderId = new URLSearchParams(location.search).get('orderId');

  // Función helper para verificar si es un servicio de máquina virtual
  // Acepta sinónimos: 'vm', 'compute'
  const isVMServiceType = (type: string | undefined): boolean => {
    if (!type) return false;
    const normalizedType = type.toLowerCase();
    return normalizedType === 'vm' || normalizedType === 'compute' || normalizedType === 'virtual-machine';
  };

  // Función helper para verificar si el servicio está en proceso de creación
  const isServiceCreating = (status: string | undefined): boolean => {
    if (!status) return false;
    const normalizedStatus = status.toLowerCase();
    return normalizedStatus === 'deploying' || 
           normalizedStatus === 'initializing' || 
           normalizedStatus === 'stacking' || 
           normalizedStatus === 'creating';
  };

  // Función helper para verificar si el servicio está en proceso de eliminación (solo 'deleting', no 'deleted')
  const isServiceDeleting = (status: string | undefined): boolean => {
    if (!status) return false;
    const normalizedStatus = status.toLowerCase();
    return normalizedStatus === 'deleting';
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('No se proporcionó un ID de servicio');
        setLoading(false);
        return;
      }

      // Prevenir llamadas duplicadas: si ya se procesó este id, no hacer nada
      if (hasFetchedRef.current === id) {
        return;
      }

      // Cancelar cualquier petición anterior
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Crear nuevo AbortController para esta petición
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Marcar que estamos procesando este id
      hasFetchedRef.current = id;

      try {
        setLoading(true);
        setError(null);

        // Obtener el servicio
        const serviceData: any = await servicesService.getById(id);
        
        // Verificar si la petición fue cancelada
        if (abortController.signal.aborted) return;
        
        // Verificar que sea de tipo VM (acepta sinónimos: 'vm', 'compute')
        if (!isVMServiceType(serviceData.type)) {
          setError('Este servicio no es una máquina virtual');
          setLoading(false);
          return;
        }

        // Guardar información del producto si viene en la respuesta
        if (serviceData.product) {
          setProduct(serviceData.product);
        }

        // Extraer vmConfig de metadata si está disponible
        if (serviceData.metadata?.vmConfig) {
          setVmConfig(serviceData.metadata.vmConfig);
        }

        // Normalizar el status a minúsculas si viene en mayúsculas
        const normalizedStatus = serviceData.status?.toLowerCase() || serviceData.status;
        
        // Mapear el servicio con el status normalizado y extraer instanceType y storage de vmConfig
        const serviceVmConfig = serviceData.metadata?.vmConfig || {};
        const mappedService: VMService = {
          ...serviceData,
          type: 'vm', // Normalizar el tipo
          status: normalizedStatus as any,
          os: serviceData.product?.os || serviceData.os || 'Unknown',
          instanceType: serviceVmConfig.instanceType || serviceData.instanceType,
          // Mapear publicIp del response (viene como publicIp en el API)
          // Si access es PUBLIC_IP, usar publicIp, sino null
          ipAddress: serviceData.access === 'PUBLIC_IP' ? (serviceData.publicIp || serviceData.ipAddress) : null,
          // Agregar nuevos campos del response
          privateIp: serviceData.privateIp,
          privateDnsName: serviceData.privateDnsName,
          publicIp: serviceData.publicIp,
          publicDnsName: serviceData.publicDnsName,
          publicDnsNameKubiy: serviceData.publicDnsNameKubiy,
          access: serviceData.access,
          // Credenciales de Windows
          windowsCreds: serviceData.windowsCreds,
          // Credenciales de Linux (clave SSH)
          linuxCreds: serviceData.linuxCreds,
          // storage no está en VMService, pero lo guardamos en vmConfig
        };

        setService(mappedService);

        // Mapear reglas de seguridad del API
        if (serviceData.securityRules) {
          const mappedIngressRules = mapSecurityRulesFromAPI(serviceData.securityRules, 'ingress');
          const mappedEgressRules = mapSecurityRulesFromAPI(serviceData.securityRules, 'egress');
          setIngressRules(mappedIngressRules);
          setEgressRules(mappedEgressRules);
          // Guardar también las reglas originales para comparar cambios
          setOriginalIngressRules(mappedIngressRules);
          setOriginalEgressRules(mappedEgressRules);
        }

        // Si hay orderId, obtener la orden
        if (orderId) {
          try {
            const orderData = await ordersService.getById(orderId);
            
            // Verificar si la petición fue cancelada
            if (abortController.signal.aborted) return;
            
            // Verificar que la orden sea de tipo VM (acepta sinónimos: 'virtual-machine', 'VM', 'Compute')
            const orderType = orderData.metadata?.type?.toLowerCase();
            if (orderType === 'virtual-machine' || orderType === 'vm' || orderType === 'compute') {
              setOrder(orderData);
            }
          } catch (err: any) {
            // Ignorar errores de cancelación
            if (abortController.signal.aborted) return;
            console.error('Error fetching order:', err);
          }
        }

      } catch (err: any) {
        // Ignorar errores de cancelación
        if (abortController.signal.aborted) return;
        
        // Si el servicio no existe (404), intentar obtener la orden
        const isNotFound = err?.response?.status === 404 || err?.status === 404 || err?.message?.includes('404');
        
        if (isNotFound) {
          // Intentar obtener la orden por orderId o buscar por serviceId
          try {
            let orderData: Order | null = null;
            
            // Si hay orderId en la URL, obtener la orden directamente
            if (orderId) {
              orderData = await ordersService.getById(orderId);
            } else {
              // Si no hay orderId, intentar buscar órdenes con este serviceId
              // Nota: Esto requeriría un endpoint adicional, por ahora solo usamos orderId
              // En el futuro se podría agregar un endpoint para buscar orden por serviceId
            }
            
            if (orderData) {
              // Verificar que la orden sea de tipo VM
              const orderType = orderData.metadata?.type?.toLowerCase();
              if (orderType === 'virtual-machine' || orderType === 'vm' || orderType === 'compute') {
                setOrder(orderData);
                // No establecer error, solo mostrar la pantalla de creación
                setError(null);
              } else {
                setError('La orden no es de tipo máquina virtual');
              }
            } else {
              setError('Servicio no encontrado y no se pudo obtener información de la orden');
            }
          } catch (orderErr: any) {
            console.error('Error fetching order:', orderErr);
            setError('Servicio no encontrado. El servicio puede estar en proceso de creación.');
          }
        } else {
          setError(err?.message || 'Error al cargar la máquina virtual');
        }
        
        // Resetear el flag solo en caso de error para permitir reintento
        if (hasFetchedRef.current === id) {
          hasFetchedRef.current = null;
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup: cancelar peticiones solo si el id realmente cambia
    return () => {
      // Solo cancelar si el id cambió (no en el primer cleanup de StrictMode)
      // El abortController se maneja automáticamente cuando se crea uno nuevo
    };
  }, [id, orderId]);

  // Polling para verificar si el servicio se ha creado cuando no existe pero sí la orden
  useEffect(() => {
    if (service || !order || !id || loading) return;

    const interval = setInterval(async () => {
      try {
        // Intentar obtener el servicio
        const serviceData: any = await servicesService.getById(id);
        
        // Si el servicio ahora existe, procesarlo
        if (serviceData && isVMServiceType(serviceData.type)) {
          // Guardar información del producto si viene en la respuesta
          if (serviceData.product) {
            setProduct(serviceData.product);
          }

          // Extraer vmConfig de metadata si está disponible
          if (serviceData.metadata?.vmConfig) {
            setVmConfig(serviceData.metadata.vmConfig);
            
            // Las reglas de seguridad ahora se manejan con ingressRules y egressRules
          }

          // Normalizar el status
          const normalizedStatus = serviceData.status?.toLowerCase() || serviceData.status;
          
          const serviceVmConfig = serviceData.metadata?.vmConfig || {};
          const mappedService: VMService = {
            ...serviceData,
            type: 'vm',
            status: normalizedStatus as any,
            os: serviceData.product?.os || serviceData.os || 'Unknown',
            instanceType: serviceVmConfig.instanceType || serviceData.instanceType,
            // Mapear publicIp del response (viene como publicIp en el API)
            // Si access es PUBLIC_IP, usar publicIp, sino null
            ipAddress: serviceData.access === 'PUBLIC_IP' ? (serviceData.publicIp || serviceData.ipAddress) : null,
            // Agregar nuevos campos del response
            privateIp: serviceData.privateIp,
            privateDnsName: serviceData.privateDnsName,
            publicIp: serviceData.publicIp,
            publicDnsName: serviceData.publicDnsName,
            publicDnsNameKubiy: serviceData.publicDnsNameKubiy,
            access: serviceData.access,
            windowsCreds: serviceData.windowsCreds,
            linuxCreds: serviceData.linuxCreds,
          };

          setService(mappedService);
          
          // Mapear reglas de seguridad del API
          if (serviceData.securityRules) {
            const mappedIngressRules = mapSecurityRulesFromAPI(serviceData.securityRules, 'ingress');
            const mappedEgressRules = mapSecurityRulesFromAPI(serviceData.securityRules, 'egress');
            setIngressRules(mappedIngressRules);
            setEgressRules(mappedEgressRules);
            // Guardar también las reglas originales para comparar cambios
            setOriginalIngressRules(mappedIngressRules);
            setOriginalEgressRules(mappedEgressRules);
          }
          
          setError(null);
          
          // Obtener eventos del servicio (una sola vez)
          try {
            const serviceEventsResponse = await servicesService.getEvents(id);
            const formattedEvents = mapServiceEventsToUI(serviceEventsResponse);
            setEvents(formattedEvents);
          } catch (eventsError) {
            console.warn('Error al obtener eventos:', eventsError);
          }
          
          // Detener el polling ya que el servicio existe
          clearInterval(interval);
        } else {
          // Si el servicio aún no existe, intentar obtener eventos
          try {
            const serviceEventsResponse = await servicesService.getEvents(id);
            const formattedEvents = mapServiceEventsToUI(serviceEventsResponse);
            setEvents(formattedEvents);
          } catch (eventsError) {
            // Si no hay eventos aún, no es crítico
          }
        }
      } catch (err: any) {
        // Si sigue siendo 404, el servicio aún no existe, continuar polling
        const isNotFound = err?.response?.status === 404 || err?.status === 404;
        if (!isNotFound) {
          console.error('Error polling service:', err);
        }
      }
    }, 10000); // Poll cada 10 segundos

    return () => clearInterval(interval);
  }, [service, order, id, loading]);

  // Polling para monitorear el estado cuando se está eliminando
  // Se activa cuando isDeleting es true O cuando el servicio está en estado 'deleting'
  useEffect(() => {
    if (!id || !service) return;
    
    const normalizedStatus = service.status?.toLowerCase() || '';
    const shouldMonitor = isDeleting || normalizedStatus === 'deleting';
    
    if (!shouldMonitor) return;

    const interval = setInterval(async () => {
      try {
        // Intentar obtener el servicio
        const serviceData: any = await servicesService.getById(id);
        
        // Si el servicio existe, verificar su estado
        if (serviceData) {
          const normalizedStatus = serviceData.status?.toLowerCase() || '';
          
          // Si el estado es 'deleted', 'terminated' o 'deleting' (y luego cambia a deleted), el servicio está completamente eliminado
          if (normalizedStatus === 'deleted' || normalizedStatus === 'terminated') {
            clearInterval(interval);
            setIsDeleting(false);
            setIsProcessing(false);
            navigate('/virtual-machines');
            return;
          }
          
          // Si el estado es 'deleting', continuar monitoreando
          if (normalizedStatus === 'deleting') {
            // El servicio está en proceso de eliminación, continuar monitoreando
          }
          
          // Actualizar el estado del servicio para mostrar el progreso
          if (isVMServiceType(serviceData.type)) {
            const serviceVmConfig = serviceData.metadata?.vmConfig || {};
            const mappedService: VMService = {
              ...serviceData,
              type: 'vm',
              status: normalizedStatus as any,
              os: serviceData.product?.os || serviceData.os || 'Unknown',
              instanceType: serviceVmConfig.instanceType || serviceData.instanceType,
              ipAddress: serviceData.access === 'PUBLIC_IP' ? (serviceData.publicIp || serviceData.ipAddress) : null,
              privateIp: serviceData.privateIp,
              privateDnsName: serviceData.privateDnsName,
              publicIp: serviceData.publicIp,
              publicDnsName: serviceData.publicDnsName,
              publicDnsNameKubiy: serviceData.publicDnsNameKubiy,
              access: serviceData.access,
            };
            setService(mappedService);
          }
          
          // Intentar obtener eventos para mostrar el progreso
          try {
            const serviceEventsResponse = await servicesService.getEvents(id);
            const formattedEvents = mapServiceEventsToUI(serviceEventsResponse);
            setEvents(formattedEvents);
          } catch (eventsError) {
            // Si no hay eventos, no es crítico
          }
        }
      } catch (err: any) {
        // Si el servicio no existe (404), está completamente eliminado
        const isNotFound = err?.response?.status === 404 || err?.status === 404;
        if (isNotFound) {
          clearInterval(interval);
          setIsDeleting(false);
          setIsProcessing(false);
          navigate('/virtual-machines');
          return;
        }
        
        // Si es otro error, continuar monitoreando
        console.error('Error al monitorear eliminación:', err);
      }
    }, 10000); // Poll cada 10 segundos durante la eliminación

    return () => clearInterval(interval);
  }, [isDeleting, id, service?.status, navigate]);

  // Polling para eventos si el servicio está en proceso de creación
  useEffect(() => {
    if (!service || !id) return;
    
    // Verificar si el servicio está en proceso de creación
    const normalizedStatus = service.status?.toLowerCase() || '';
    const isCreating = normalizedStatus === 'deploying' || 
                       normalizedStatus === 'initializing' || 
                       normalizedStatus === 'stacking' || 
                       normalizedStatus === 'creating';
    
    if (!isCreating) return;

    const interval = setInterval(async () => {
      try {
        // Obtener datos actualizados del servicio
        const updatedServiceData: any = await servicesService.getById(id);
        
        // Normalizar el status
        const normalizedStatus = updatedServiceData.status?.toLowerCase() || updatedServiceData.status;
        
        // Guardar información del producto si viene en la respuesta
        if (updatedServiceData.product) {
          setProduct(updatedServiceData.product);
        }

        // Extraer vmConfig de metadata si está disponible
        if (updatedServiceData.metadata?.vmConfig) {
          setVmConfig(updatedServiceData.metadata.vmConfig);
          
          // Las reglas de seguridad ahora se manejan con ingressRules y egressRules
        }
        
        const serviceVmConfig = updatedServiceData.metadata?.vmConfig || {};
        const mappedService: VMService = {
          ...updatedServiceData,
          type: 'vm',
          status: normalizedStatus as any,
          os: updatedServiceData.product?.os || updatedServiceData.os || 'Unknown',
          instanceType: serviceVmConfig.instanceType || updatedServiceData.instanceType,
          // Mapear publicIp del response (viene como publicIp en el API)
          // Si access es PUBLIC_IP, usar publicIp, sino null
          ipAddress: updatedServiceData.access === 'PUBLIC_IP' ? (updatedServiceData.publicIp || updatedServiceData.ipAddress) : null,
          // Agregar nuevos campos del response
          privateIp: updatedServiceData.privateIp,
          privateDnsName: updatedServiceData.privateDnsName,
          publicIp: updatedServiceData.publicIp,
          publicDnsName: updatedServiceData.publicDnsName,
          publicDnsNameKubiy: updatedServiceData.publicDnsNameKubiy,
          access: updatedServiceData.access,
        };
        
        setService(mappedService);
        
        // Mapear reglas de seguridad del API
        if (updatedServiceData.securityRules) {
          const mappedIngressRules = mapSecurityRulesFromAPI(updatedServiceData.securityRules, 'ingress');
          const mappedEgressRules = mapSecurityRulesFromAPI(updatedServiceData.securityRules, 'egress');
          setIngressRules(mappedIngressRules);
          setEgressRules(mappedEgressRules);
          // Guardar también las reglas originales para comparar cambios
          setOriginalIngressRules(mappedIngressRules);
          setOriginalEgressRules(mappedEgressRules);
        }
        
        // Obtener eventos actualizados
        try {
          const serviceEventsResponse = await servicesService.getEvents(id);
          const formattedEvents = mapServiceEventsToUI(serviceEventsResponse);
          setEvents(formattedEvents);
        } catch (eventsError) {
          console.warn('Error al obtener eventos durante polling:', eventsError);
        }
        
        // Si cambió el estado y ya no está en proceso de creación, detener el polling
        const stillCreating = normalizedStatus === 'deploying' || 
                             normalizedStatus === 'initializing' || 
                             normalizedStatus === 'stacking' || 
                             normalizedStatus === 'creating';
        
        if (!stillCreating) {
          clearInterval(interval);
        }
      } catch (err) {
        console.error('Error polling service status:', err);
      }
    }, 10000); // Poll cada 10 segundos

    return () => clearInterval(interval);
  }, [service?.status, id]);

  // Si el servicio está en estado 'deleting' pero no estamos monitoreando, activar el monitoreo
  useEffect(() => {
    if (!service) return;
    const normalizedStatus = service.status?.toLowerCase() || '';
    if (normalizedStatus === 'deleting' && !isDeleting) {
      setIsDeleting(true);
    }
  }, [service?.status, isDeleting]);

  const handleConnect = () => {
    if (!service || !id) {
      showError(t('notifications.vm.connectError'));
      return;
    }
    
    // Navegar a la pantalla de Terminal dentro de virtual-machines
    navigate(`/virtual-machines/terminal/${id}`);
  };

  const handleStart = async () => {
    if (!service || !id) return;
    setIsProcessing(true);
    try {
      // Ejecutar la acción de inicio
      const response = await servicesService.start(id);
      
      // Si la respuesta es exitosa (status 200), comenzar monitoreo
      if (response) {
        // Obtener el servicio actualizado inmediatamente
        const currentService = await servicesService.getById(id);
        
        // Monitorear hasta que el estado sea 'running' o 'stopped'
        const finalService = await servicesService.monitorServiceStatus(
          id,
          ['running', 'stopped', 'deleted'],
          (updatedService) => {
            // Actualizar el estado en tiempo real mientras se monitorea
            if (isVMServiceType(updatedService.type)) {
              const serviceData = updatedService as any;
              const serviceVmConfig = serviceData.metadata?.vmConfig || {};
              const normalizedStatus = (serviceData.status || 'starting')?.toLowerCase() || 'starting';
              const mappedService: VMService = {
                ...serviceData,
                type: 'vm',
                status: normalizedStatus as any,
                os: serviceData.product?.os || serviceData.os || 'Unknown',
                instanceType: serviceVmConfig.instanceType || serviceData.instanceType,
                ipAddress: serviceData.access === 'PUBLIC_IP' ? (serviceData.publicIp || serviceData.ipAddress) : null,
                privateIp: serviceData.privateIp,
                privateDnsName: serviceData.privateDnsName,
                publicIp: serviceData.publicIp,
                publicDnsName: serviceData.publicDnsName,
                publicDnsNameKubiy: serviceData.publicDnsNameKubiy,
                access: serviceData.access,
              };
              setService(mappedService);
            }
          }
        );
        
        // Actualizar con el estado final y obtener eventos una sola vez
        if (isVMServiceType(finalService.type)) {
          const serviceData = finalService as any;
          const serviceVmConfig = serviceData.metadata?.vmConfig || {};
          const normalizedStatus = (serviceData.status || '')?.toLowerCase();
          const mappedService: VMService = {
            ...serviceData,
            type: 'vm',
            status: normalizedStatus as any,
            os: serviceData.product?.os || serviceData.os || 'Unknown',
            instanceType: serviceVmConfig.instanceType || serviceData.instanceType,
            ipAddress: serviceData.access === 'PUBLIC_IP' ? (serviceData.publicIp || serviceData.ipAddress) : null,
            privateIp: serviceData.privateIp,
            privateDnsName: serviceData.privateDnsName,
            publicIp: serviceData.publicIp,
            publicDnsName: serviceData.publicDnsName,
            publicDnsNameKubiy: serviceData.publicDnsNameKubiy,
            access: serviceData.access,
          };
          setService(mappedService);
          
          // Obtener eventos actualizados una sola vez al finalizar
          try {
            const serviceEventsResponse = await servicesService.getEvents(id);
            const formattedEvents = mapServiceEventsToUI(serviceEventsResponse);
            setEvents(formattedEvents);
          } catch (eventsError) {
            // Ignorar errores de eventos
          }
        }
        
        success(t('notifications.vm.startSuccess'));
      }
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.startError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStop = async () => {
    if (!service || !id) return;
    setIsProcessing(true);
    try {
      // Ejecutar la acción de detención
      const response = await servicesService.stop(id);
      
      // Si la respuesta es exitosa (status 200), comenzar monitoreo
      if (response) {
        // Obtener el servicio actualizado inmediatamente
        const currentService = await servicesService.getById(id);
        
        // Monitorear hasta que el estado sea 'stopped' o 'running'
        const finalService = await servicesService.monitorServiceStatus(
          id,
          ['stopped', 'running', 'deleted'],
          (updatedService) => {
            // Actualizar el estado en tiempo real mientras se monitorea
            if (isVMServiceType(updatedService.type)) {
              const serviceData = updatedService as any;
              const serviceVmConfig = serviceData.metadata?.vmConfig || {};
              const normalizedStatus = (serviceData.status || 'stopping')?.toLowerCase() || 'stopping';
              const mappedService: VMService = {
                ...serviceData,
                type: 'vm',
                status: normalizedStatus as any,
                os: serviceData.product?.os || serviceData.os || 'Unknown',
                instanceType: serviceVmConfig.instanceType || serviceData.instanceType,
                ipAddress: serviceData.access === 'PUBLIC_IP' ? (serviceData.publicIp || serviceData.ipAddress) : null,
                privateIp: serviceData.privateIp,
                privateDnsName: serviceData.privateDnsName,
                publicIp: serviceData.publicIp,
                publicDnsName: serviceData.publicDnsName,
                publicDnsNameKubiy: serviceData.publicDnsNameKubiy,
                access: serviceData.access,
              };
              setService(mappedService);
            }
          }
        );
        
        // Actualizar con el estado final y obtener eventos una sola vez
        if (isVMServiceType(finalService.type)) {
          const serviceData = finalService as any;
          const serviceVmConfig = serviceData.metadata?.vmConfig || {};
          const normalizedStatus = (serviceData.status || 'stopped')?.toLowerCase() || 'stopped';
          const mappedService: VMService = {
            ...serviceData,
            type: 'vm',
            status: normalizedStatus as any,
            os: serviceData.product?.os || serviceData.os || 'Unknown',
            instanceType: serviceVmConfig.instanceType || serviceData.instanceType,
            ipAddress: serviceData.access === 'PUBLIC_IP' ? (serviceData.publicIp || serviceData.ipAddress) : null,
            privateIp: serviceData.privateIp,
            privateDnsName: serviceData.privateDnsName,
            publicIp: serviceData.publicIp,
            publicDnsName: serviceData.publicDnsName,
            publicDnsNameKubiy: serviceData.publicDnsNameKubiy,
            access: serviceData.access,
          };
          setService(mappedService);
          
          // Obtener eventos actualizados una sola vez al finalizar
          try {
            const serviceEventsResponse = await servicesService.getEvents(id);
            const formattedEvents = mapServiceEventsToUI(serviceEventsResponse);
            setEvents(formattedEvents);
          } catch (eventsError) {
            // Ignorar errores de eventos
          }
        }
        
        success(t('notifications.vm.stopSuccess'));
      }
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.stopError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRestart = async () => {
    if (!service || !id) return;
    setIsProcessing(true);
    try {
      // Ejecutar la acción de reinicio
      const response = await servicesService.restart(id);
      
      // Si la respuesta es exitosa (status 200), comenzar monitoreo
      if (response) {
        // Obtener el servicio actualizado inmediatamente
        const currentService = await servicesService.getById(id);
        
        // Monitorear hasta que el estado sea 'running' o 'stopped'
        const finalService = await servicesService.monitorServiceStatus(
          id,
          ['running', 'stopped', 'deleted'],
          (updatedService) => {
            // Actualizar el estado en tiempo real mientras se monitorea
            if (isVMServiceType(updatedService.type)) {
              const serviceData = updatedService as any;
              const serviceVmConfig = serviceData.metadata?.vmConfig || {};
              const normalizedStatus = (serviceData.status || 'restarting')?.toLowerCase() || 'restarting';
              const mappedService: VMService = {
                ...serviceData,
                type: 'vm',
                status: normalizedStatus as any,
                os: serviceData.product?.os || serviceData.os || 'Unknown',
                instanceType: serviceVmConfig.instanceType || serviceData.instanceType,
                ipAddress: serviceData.access === 'PUBLIC_IP' ? (serviceData.publicIp || serviceData.ipAddress) : null,
                privateIp: serviceData.privateIp,
                privateDnsName: serviceData.privateDnsName,
                publicIp: serviceData.publicIp,
                publicDnsName: serviceData.publicDnsName,
                publicDnsNameKubiy: serviceData.publicDnsNameKubiy,
                access: serviceData.access,
              };
              setService(mappedService);
            }
          }
        );
        
        // Actualizar con el estado final y obtener eventos una sola vez
        if (isVMServiceType(finalService.type)) {
          const serviceData = finalService as any;
          const serviceVmConfig = serviceData.metadata?.vmConfig || {};
          const normalizedStatus = (serviceData.status || '')?.toLowerCase();
          const mappedService: VMService = {
            ...serviceData,
            type: 'vm',
            status: normalizedStatus as any,
            os: serviceData.product?.os || serviceData.os || 'Unknown',
            instanceType: serviceVmConfig.instanceType || serviceData.instanceType,
            ipAddress: serviceData.access === 'PUBLIC_IP' ? (serviceData.publicIp || serviceData.ipAddress) : null,
            privateIp: serviceData.privateIp,
            privateDnsName: serviceData.privateDnsName,
            publicIp: serviceData.publicIp,
            publicDnsName: serviceData.publicDnsName,
            publicDnsNameKubiy: serviceData.publicDnsNameKubiy,
            access: serviceData.access,
          };
          setService(mappedService);
          
          // Obtener eventos actualizados una sola vez al finalizar
          try {
            const serviceEventsResponse = await servicesService.getEvents(id);
            const formattedEvents = mapServiceEventsToUI(serviceEventsResponse);
            setEvents(formattedEvents);
          } catch (eventsError) {
            // Ignorar errores de eventos
          }
        }
        
        success(t('notifications.vm.restartSuccess'));
      }
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.restartError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRefreshEvents = async () => {
    if (!id || isRefreshingEvents) return;
    
    setIsRefreshingEvents(true);
    try {
      const serviceEventsResponse = await servicesService.getEvents(id);
      const formattedEvents = mapServiceEventsToUI(serviceEventsResponse);
      setEvents(formattedEvents);
      success(t('virtualMachines.detail.events.refreshed') || 'Eventos actualizados');
    } catch (err: any) {
      console.error('Error al refrescar eventos:', err);
      showError(err?.message || t('virtualMachines.detail.events.refreshError') || 'Error al actualizar eventos');
    } finally {
      setIsRefreshingEvents(false);
    }
  };

  const handleDelete = async () => {
    if (!service || !id) return;
    setIsProcessing(true);
    setIsDeleting(true);
    setShowDeleteModal(false);
    
    try {
      // Llamar al endpoint DELETE
      await servicesService.delete(id);
      
      // El servicio comenzará a eliminarse, ahora monitoreamos su estado
      // El useEffect de polling se encargará de verificar cuando esté completamente eliminado
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.deleteError') + ': ' + (err?.response?.data?.message || 'Error desconocido'));
      setIsProcessing(false);
      setIsDeleting(false);
    }
  };

  // Función helper para verificar si es Windows
  const isWindows = (): boolean => {
    if (!service && !product) return false;
    const osName = (product?.os || product?.name || product?.osFamily || service?.os || '').toLowerCase();
    return osName.includes('windows');
  };

  // Función helper para verificar si es Linux
  const isLinux = (): boolean => {
    if (!service && !product) return false;
    const osName = (product?.os || product?.name || product?.osFamily || service?.os || '').toLowerCase();
    return osName.includes('linux') || osName.includes('ubuntu') || osName.includes('rhel') || osName.includes('red hat') || osName.includes('centos') || osName.includes('amazon');
  };

  // Usuario SSH según sistema operativo Linux (para documentación/conexión)
  const getSshUserForOs = (): string => {
    const osName = (product?.os || product?.name || product?.osFamily || service?.os || '').toLowerCase();
    if (osName.includes('ubuntu')) return 'ubuntu';
    if (osName.includes('amazon')) return 'ec2-user';
    if (osName.includes('debian')) return 'admin';
    if (osName.includes('rhel') || osName.includes('red hat')) return 'ec2-user';
    if (osName.includes('centos')) return 'centos';
    return 'ec2-user'; // default para Linux genérico/Amazon
  };

  // Función helper para verificar si las credenciales ya fueron descargadas
  const areCredentialsDelivered = (): boolean => {
    if (!service) return false;
    return !!(service.windowsCreds?.deliveredAt || service.windowsCreds?.deliveredBy);
  };

  // Función helper para verificar si la clave SSH ya fue descargada
  const isLinuxKeyDelivered = (): boolean => {
    if (!service) return false;
    return !!(service.linuxSSH?.deliveredAt || service.linuxSSH?.deliveredBy);
  };

  // Función para descargar credenciales de Windows
  const handleDownloadWindowsCredentials = async () => {
    if (!service || !id) return;
    
    setIsDownloadingCredentials(true);
    try {
      // Descargar el archivo como blob
      const blob = await servicesService.downloadWindowsCredentials(id);
      
      // Crear un enlace temporal para descargar el archivo
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${service.name || 'windows-credentials'}-credentials.txt`;
      document.body.appendChild(link);
      link.click();
      
      // Limpiar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Actualizar el servicio para reflejar que las credenciales fueron descargadas
      // Recargar el servicio para obtener los datos actualizados
      const updatedService = await servicesService.getById(id);
      if (isVMServiceType(updatedService.type)) {
        const serviceData = updatedService as any;
        const serviceVmConfig = serviceData.metadata?.vmConfig || {};
        const normalizedStatus = (serviceData.status || 'running')?.toLowerCase() || 'running';
        const mappedService: VMService = {
          ...serviceData,
          type: 'vm',
          status: normalizedStatus as any,
          os: serviceData.product?.os || serviceData.os || 'Unknown',
          instanceType: serviceVmConfig.instanceType || serviceData.instanceType,
          ipAddress: serviceData.access === 'PUBLIC_IP' ? (serviceData.publicIp || serviceData.ipAddress) : null,
          privateIp: serviceData.privateIp,
privateDnsName: serviceData.privateDnsName,
            publicIp: serviceData.publicIp,
            publicDnsName: serviceData.publicDnsName,
            publicDnsNameKubiy: serviceData.publicDnsNameKubiy,
            access: serviceData.access,
            windowsCreds: serviceData.windowsCreds, // Incluir las credenciales de Windows
          linuxCreds: serviceData.linuxCreds,
        };
        setService(mappedService);
      }
      
      success(t('notifications.vm.windowsCredentialsDownloaded') || 'Credenciales de Windows descargadas exitosamente');
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.windowsCredentialsError') || 'Error al descargar credenciales de Windows');
    } finally {
      setIsDownloadingCredentials(false);
    }
  };

  // Función para descargar clave SSH (Linux)
  const handleDownloadLinuxSshKey = async () => {
    if (!service || !id) return;
    
    setIsDownloadingSshKey(true);
    try {
      const blob = await servicesService.downloadLinuxSshKey(id);
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${service.name || 'vm-ssh-key'}.pem`;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      const updatedService = await servicesService.getById(id);
      if (isVMServiceType(updatedService.type)) {
        const serviceData = updatedService as any;
        const serviceVmConfig = serviceData.metadata?.vmConfig || {};
        const normalizedStatus = (serviceData.status || 'running')?.toLowerCase() || 'running';
        const mappedService: VMService = {
          ...serviceData,
          type: 'vm',
          status: normalizedStatus as any,
          os: serviceData.product?.os || serviceData.os || 'Unknown',
          instanceType: serviceVmConfig.instanceType || serviceData.instanceType,
          ipAddress: serviceData.access === 'PUBLIC_IP' ? (serviceData.publicIp || serviceData.ipAddress) : null,
          privateIp: serviceData.privateIp,
          privateDnsName: serviceData.privateDnsName,
          publicIp: serviceData.publicIp,
          publicDnsName: serviceData.publicDnsName,
          publicDnsNameKubiy: serviceData.publicDnsNameKubiy,
          access: serviceData.access,
          windowsCreds: serviceData.windowsCreds,
          linuxCreds: serviceData.linuxCreds,
        };
        setService(mappedService);
      }
      
      success(t('notifications.vm.linuxSshKeyDownloaded') || 'Clave SSH descargada exitosamente');
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.linuxSshKeyError') || 'Error al descargar clave SSH');
    } finally {
      setIsDownloadingSshKey(false);
    }
  };

  // Función helper para comparar reglas (ignora el id y la descripción)
  const areRulesEqual = (
    rule1: { protocol: string; fromPort: number; toPort: number; cidr: string },
    rule2: { protocol: string; fromPort: number; toPort: number; cidr: string }
  ): boolean => {
    return (
      rule1.protocol === rule2.protocol &&
      rule1.fromPort === rule2.fromPort &&
      rule1.toPort === rule2.toPort &&
      rule1.cidr === rule2.cidr
    );
  };

  const handleSaveIngressRules = async () => {
    if (!service || !id) return;
    
    setIsSavingIngress(true);
    try {
      // Preparar reglas actuales (sin id)
      const currentRules = ingressRules.map(rule => ({
        protocol: rule.protocol,
        fromPort: rule.fromPort,
        toPort: rule.toPort,
        cidr: rule.cidr,
        description: rule.description,
      }));

      // Preparar reglas originales (sin id)
      const originalRules = originalIngressRules.map(rule => ({
        protocol: rule.protocol,
        fromPort: rule.fromPort,
        toPort: rule.toPort,
        cidr: rule.cidr,
        description: rule.description,
      }));

      // Encontrar reglas a agregar (están en currentRules pero no en originalRules)
      const rulesToAdd = currentRules.filter(currentRule =>
        !originalRules.some(originalRule => areRulesEqual(originalRule, currentRule))
      );

      // Encontrar reglas a eliminar (están en originalRules pero no en currentRules)
      const rulesToRemove = originalRules.filter(originalRule =>
        !currentRules.some(currentRule => areRulesEqual(originalRule, currentRule))
      );

      // Enviar POST con dos objetos: uno para agregar y otro para eliminar
      if (rulesToAdd.length > 0 || rulesToRemove.length > 0) {
        await servicesService.updateSecurityRulesBatch(id, 'ingress', rulesToAdd, rulesToRemove);
      }
      
      // Luego guardar la lista completa en base de datos (PATCH sin mode)
      await servicesService.saveSecurityRules(id, 'ingress', currentRules);
      
      // Actualizar las reglas originales con las actuales
      setOriginalIngressRules(ingressRules);
      
      success(t('notifications.vm.securityRulesSuccess'));
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.securityRulesError'));
    } finally {
      setIsSavingIngress(false);
    }
  };

  const handleSaveEgressRules = async () => {
    if (!service || !id) return;
    
    setIsSavingEgress(true);
    try {
      // Preparar reglas actuales (sin id)
      const currentRules = egressRules.map(rule => ({
        protocol: rule.protocol,
        fromPort: rule.fromPort,
        toPort: rule.toPort,
        cidr: rule.cidr,
        description: rule.description,
      }));

      // Preparar reglas originales (sin id)
      const originalRules = originalEgressRules.map(rule => ({
        protocol: rule.protocol,
        fromPort: rule.fromPort,
        toPort: rule.toPort,
        cidr: rule.cidr,
        description: rule.description,
      }));

      // Encontrar reglas a agregar (están en currentRules pero no en originalRules)
      const rulesToAdd = currentRules.filter(currentRule =>
        !originalRules.some(originalRule => areRulesEqual(originalRule, currentRule))
      );

      // Encontrar reglas a eliminar (están en originalRules pero no en currentRules)
      const rulesToRemove = originalRules.filter(originalRule =>
        !currentRules.some(currentRule => areRulesEqual(originalRule, currentRule))
      );

      // Enviar POST con dos objetos: uno para agregar y otro para eliminar
      if (rulesToAdd.length > 0 || rulesToRemove.length > 0) {
        await servicesService.updateSecurityRulesBatch(id, 'egress', rulesToAdd, rulesToRemove);
      }
      
      // Luego guardar la lista completa en base de datos (PATCH sin mode)
      await servicesService.saveSecurityRules(id, 'egress', currentRules);
      
      // Actualizar las reglas originales con las actuales
      setOriginalEgressRules(egressRules);
      
      success(t('notifications.vm.securityRulesSuccess'));
    } catch (err: any) {
      showError(err?.message || t('notifications.vm.securityRulesError'));
    } finally {
      setIsSavingEgress(false);
    }
  };

  // Mapeo de tipos a puertos y protocolos
  const getTypeConfig = (type: SecurityRule['type']): { fromPort: number; toPort: number; protocol: 'tcp' | 'udp' } => {
    const configs: Record<SecurityRule['type'], { fromPort: number; toPort: number; protocol: 'tcp' | 'udp' }> = {
      'RDP': { fromPort: 3389, toPort: 3389, protocol: 'tcp' },
      'MSSQL': { fromPort: 1433, toPort: 1433, protocol: 'tcp' },
      'MySQL': { fromPort: 3306, toPort: 3306, protocol: 'tcp' },
      'PostgreSQL': { fromPort: 5432, toPort: 5432, protocol: 'tcp' },
      'SSH': { fromPort: 22, toPort: 22, protocol: 'tcp' },
      'HTTP': { fromPort: 80, toPort: 80, protocol: 'tcp' },
      'HTTPS': { fromPort: 443, toPort: 443, protocol: 'tcp' },
      'Custom': { fromPort: 0, toPort: 0, protocol: 'tcp' },
    };
    return configs[type];
  };


  // Obtener IP del usuario
  const getUserIP = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('Error obteniendo IP del usuario:', error);
      return '';
    }
  };

  // Manejar cambio de tipo (auto-rellenar puerto y protocolo)
  const handleTypeChange = (type: SecurityRule['type']) => {
    const config = getTypeConfig(type);
    setNewRule({ ...newRule, type, fromPort: config.fromPort, toPort: config.toPort, protocol: config.protocol });
  };

  // Agregar nueva regla
  const handleAddRule = () => {
    if (!newRule.cidr.trim() || !newRule.description.trim() || !newRule.fromPort || !newRule.toPort) {
      showError(t('notifications.vm.requiredFieldsError'));
      return;
    }
    const rule: SecurityRule = {
      id: `rule-${Date.now()}-${Math.random()}`,
      ...newRule,
    };
    
    // Agregar a la lista correspondiente según la dirección
    if (newRule.direction === 'ingress') {
      setIngressRules([...ingressRules, rule]);
    } else {
      setEgressRules([...egressRules, rule]);
    }
    
    // Resetear formulario
    setNewRule({
      direction: 'ingress',
      type: 'SSH',
      fromPort: 22,
      toPort: 22,
      protocol: 'tcp',
      cidr: '',
      description: '',
    });
    setShowAddRuleModal(false);
  };

  // Eliminar regla
  const handleDeleteRule = (id: string, direction: 'ingress' | 'egress') => {
    if (direction === 'ingress') {
      setIngressRules(ingressRules.filter(rule => rule.id !== id));
    } else {
      setEgressRules(egressRules.filter(rule => rule.id !== id));
    }
  };

  // Editar regla
  const handleEditRule = (rule: SecurityRule) => {
    setEditingRule(rule);
    setNewRule({
      direction: rule.direction,
      type: rule.type,
      fromPort: rule.fromPort,
      toPort: rule.toPort,
      protocol: rule.protocol,
      cidr: rule.cidr,
      description: rule.description,
    });
    setShowAddRuleModal(true);
  };

  // Actualizar regla (solo permite modificar la descripción)
  const handleUpdateRule = () => {
    if (!editingRule) return;
    
    if (!newRule.description.trim()) {
      showError(t('notifications.vm.requiredFieldsError'));
      return;
    }
    
    // Actualizar solo la descripción, manteniendo todos los demás campos de la regla original
    const updatedRule: SecurityRule = {
      ...editingRule,
      description: newRule.description, // Solo actualizar la descripción
    };
    
    // Actualizar la regla en la lista correspondiente
    if (editingRule.direction === 'ingress') {
      setIngressRules(ingressRules.map(rule => 
        rule.id === editingRule.id ? updatedRule : rule
      ));
    } else {
      setEgressRules(egressRules.map(rule => 
        rule.id === editingRule.id ? updatedRule : rule
      ));
    }
    
    // Resetear formulario
    setEditingRule(null);
    setNewRule({
      direction: 'ingress',
      type: 'SSH',
      fromPort: 22,
      toPort: 22,
      protocol: 'tcp',
      cidr: '',
      description: '',
    });
    setShowAddRuleModal(false);
  };

  // Agregar mi IP
  const handleAddMyIP = async () => {
    const userIP = await getUserIP();
    if (userIP) {
      setNewRule({ ...newRule, cidr: `${userIP}/32` });
    } else {
      showError(t('notifications.vm.ipError'));
    }
  };

  // Detectar si hay un evento de eliminación de Kubiy
  const hasDeletionEvent = events.some(event => event.isDeletionEvent);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900/50 py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <div className="text-center">
          <LoadingOutlined className="text-4xl text-gray-400 dark:text-gray-500 mb-4" spin />
          <p className="text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Si no hay servicio pero sí hay orden, mostrar pantalla de creación
  if (!service && order && !error) {
    const orderVmConfig = order.metadata?.vmConfig || {};
    const orderType = order.metadata?.type?.toLowerCase();
    const isVMOrder = orderType === 'virtual-machine' || orderType === 'vm' || orderType === 'compute';
    
    if (isVMOrder) {
      return (
        <ServiceCreatingScreen
          order={order}
          serviceId={order.serviceId}
          serviceType={order.metadata?.type}
          events={events}
          renderOrderDetails={(order) => {
            const orderVmConfig = order.metadata?.vmConfig || {};
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('orders.detail.orderId')}</label>
                  <code className="text-sm font-mono text-gray-900 dark:text-white">{order.id}</code>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('orders.detail.status')}</label>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    order.status === 'PAID' 
                      ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' 
                      : order.status === 'PENDING'
                      ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                  }`}>
                    {order.status === 'PAID' ? `✓ ${t('orders.detail.paid')}` : order.status === 'PENDING' ? `⏳ ${t('orders.detail.pending')}` : order.status}
                  </span>
                </div>
                {orderVmConfig.instanceType && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('virtualMachines.detail.instanceType')}</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{orderVmConfig.instanceType}</p>
                  </div>
                )}
                {orderVmConfig.storage && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('virtualMachines.create.step1.storage')}</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{orderVmConfig.storage} GB</p>
                  </div>
                )}
                {order.metadata?.region && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('virtualMachines.detail.region')}</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{order.metadata.region}</p>
                  </div>
                )}
                {order.createdAt && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('orders.detail.createdAt')}</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {new Date(order.createdAt).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            );
          }}
          onServiceReady={(serviceId) => {
            // Recargar la página para mostrar el servicio
            window.location.reload();
          }}
          onBack={() => navigate(-1)}
          maxPollingAttempts={4}
          pollingInterval={30000}
        />
      );
    }
  }

  // Si hay error y no hay servicio ni orden, mostrar error
  if (error || !service) {
    return (
      <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900/50 py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error || t('virtualMachines.detail.title')}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-dark-bg dark:hover:bg-gray-200"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  // Usar vmConfig del servicio primero, luego de la orden como fallback
  const serviceVmConfig = vmConfig || order?.metadata?.vmConfig;
  const isDeploying = isServiceCreating(service.status);
  const isDeletingStatus = isServiceDeleting(service.status);
  // El botón de conectar solo requiere que el servicio esté running, no necesita ipAddress
  const canConnect = service.status === 'running';
  
  // Verificar si el servicio está en estado de eliminación
  const normalizedStatus = service.status?.toLowerCase() || '';
  const isDeletingOrDeleted = normalizedStatus === 'deleting' || normalizedStatus === 'deleted';

  const InfoRow = ({ icon, label, value }: { icon?: React.ReactNode; label: string; value: React.ReactNode }) => (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
      {icon && <span className="text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0">{icon}</span>}
      <span className="text-sm text-gray-500 dark:text-gray-400 w-36 flex-shrink-0">{label}</span>
      <span className="text-sm font-medium text-gray-900 dark:text-white flex-1">{value}</span>
    </div>
  );

  const CopyCode = ({ text, title }: { text: string; title: string }) => (
    <span className="flex items-center gap-1.5">
      <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded font-mono">{text}</code>
      <button
        onClick={() => navigator.clipboard.writeText(text)}
        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        title={title}
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </button>
    </span>
  );

  return (<>
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      <DetailPageHeader
        backLabel={t('common.back')}
        onBack={() => navigate(-1)}
        title={service.name}
        subtitle={`${product?.name || product?.os || t('virtualMachines.title')} • ${t('virtualMachines.detail.created')} ${getServiceAge(service.createdAt)}`}
        icon={getOSImage(product, service.os) ? (
          <img
            src={getOSImage(product, service.os)!}
            alt={product?.name || product?.os || service.os || 'OS'}
            className="w-full h-full object-contain"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : undefined}
        actions={
          <>
            {canConnect && !isWindows() && (
              <HeaderActionButton variant="primary" icon={<CodeOutlined />} onClick={handleConnect}>
                {t('virtualMachines.detail.connect')}
              </HeaderActionButton>
            )}
            {service.status === 'stopped' && (
              <HeaderActionButton variant="success" icon={<PlayCircleOutlined />} onClick={handleStart} disabled={isProcessing}>
                {t('virtualMachines.detail.start')}
              </HeaderActionButton>
            )}
            {service.status === 'running' && (
              <>
                <HeaderActionButton variant="secondary" icon={<PauseCircleOutlined />} onClick={handleStop} disabled={isProcessing}>
                  {t('virtualMachines.detail.stop')}
                </HeaderActionButton>
                <HeaderActionButton variant="secondary" icon={<SyncOutlined />} onClick={handleRestart} disabled={isProcessing || isDeletingOrDeleted}>
                  {t('virtualMachines.detail.restart')}
                </HeaderActionButton>
              </>
            )}
            <HeaderActionMenu
              items={[
                {
                  key: 'refresh',
                  label: t('virtualMachines.detail.refresh'),
                  icon: <ReloadOutlined />,
                  onClick: () => window.location.reload(),
                  disabled: isDeletingOrDeleted,
                },
                {
                  key: 'delete',
                  label: t('virtualMachines.detail.delete'),
                  icon: <DeleteOutlined />,
                  onClick: () => setShowDeleteModal(true),
                  danger: true,
                  disabled: isProcessing || isDeletingOrDeleted,
                },
              ]}
            />
          </>
        }
        statusBanners={
          <>
            {isDeploying && (
              <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded-r-lg">
                <div className="flex items-center gap-3">
                  <LoadingOutlined spin className="text-blue-600 dark:text-blue-400 text-xl" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-300">{t('virtualMachines.detail.creating')}</p>
                    <p className="text-sm text-blue-800 dark:text-blue-200 mt-0.5">{t('virtualMachines.detail.creatingDescription')}</p>
                  </div>
                </div>
              </div>
            )}
            {isDeletingStatus && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 dark:border-red-500 p-4 rounded-r-lg">
                <div className="flex items-center gap-3">
                  <LoadingOutlined spin className="text-red-600 dark:text-red-400 text-xl" />
                  <div>
                    <p className="text-sm font-semibold text-red-900 dark:text-red-300">{t('virtualMachines.detail.deleting')}</p>
                    <p className="text-sm text-red-800 dark:text-red-200 mt-0.5">{t('virtualMachines.detail.deletingDescription')}</p>
                  </div>
                </div>
              </div>
            )}
            {hasDeletionEvent && !isDeletingStatus && (
              <div className="mb-6 bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-400 dark:border-orange-500 p-4 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <WarningOutlined className="text-orange-600 dark:text-orange-400 text-xl flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-orange-900 dark:text-orange-300">{t('virtualMachines.detail.events.deletionWarning.title')}</p>
                    <p className="text-sm text-orange-800 dark:text-orange-200 mt-1">{t('virtualMachines.detail.events.deletionWarning.message')}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        }
      />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-8 space-y-4 sm:space-y-6">
          {/* Overview Card */}
          <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{t('virtualMachines.detail.overview')}</h2>
              <ServiceStatusBadge status={service.status} size="md" showLabel />
            </div>
            <div className="p-4 sm:p-6 space-y-6">
              {/* Resource tiles */}
              {(() => {
                const instanceType = serviceVmConfig?.instanceType || service.instanceType;
                const planSpecs = instanceType ? VM_EC2_SPECS[instanceType] : null;
                const storageMB = serviceVmConfig?.storage;
                if (!planSpecs && !storageMB) return null;
                return (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Recursos</h4>
                    <div className={`grid gap-3 ${planSpecs ? 'grid-cols-3' : 'grid-cols-1'}`}>
                      {planSpecs && (
                        <>
                          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 flex flex-col items-center gap-1 text-center">
                            <ThunderboltOutlined className="text-blue-500 text-lg" />
                            <span className="text-xl font-bold text-gray-900 dark:text-white">{planSpecs.vcpu}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">vCPU</span>
                          </div>
                          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 flex flex-col items-center gap-1 text-center">
                            <DatabaseOutlined className="text-purple-500 text-lg" />
                            <span className="text-xl font-bold text-gray-900 dark:text-white">{formatVMMemory(planSpecs.memoryMB)}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">RAM</span>
                          </div>
                        </>
                      )}
                      {storageMB && (
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 flex flex-col items-center gap-1 text-center">
                          <HddOutlined className="text-green-500 text-lg" />
                          <span className="text-xl font-bold text-gray-900 dark:text-white">{storageMB} GB</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Disco</span>
                        </div>
                      )}
                    </div>
                    {instanceType && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        Instancia: <span className="font-mono">{instanceType}</span> · AWS EC2 · {VM_REGION_NAMES[service.region || ''] ?? service.region ?? '—'}
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Identidad */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Identidad</h4>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  <InfoRow icon={<CodeOutlined />} label="Service ID" value={<CopyCode text={service.id} title="Copy ID" />} />
                  <InfoRow icon={<EnvironmentOutlined />} label="Sistema" value={product?.os || product?.name || service.os || 'N/A'} />
                  <InfoRow icon={<EnvironmentOutlined />} label="Región" value={VM_REGION_NAMES[service.region || ''] ?? service.region ?? '—'} />
                  <InfoRow
                    icon={<CalendarOutlined />}
                    label="Creado"
                    value={
                      <span>
                        {new Date(service.createdAt).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
                        <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">({getServiceAge(service.createdAt)})</span>
                      </span>
                    }
                  />
                  {service.access && (
                    <InfoRow
                      icon={<InfoCircleOutlined />}
                      label={t('virtualMachines.detail.accessMethod')}
                      value={
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          service.access === 'PUBLIC_IP'
                            ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                            : 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                        }`}>
                          {service.access === 'PUBLIC_IP' ? t('virtualMachines.detail.publicAccess') : t('virtualMachines.detail.ssmAccess')}
                        </span>
                      }
                    />
                  )}
                </div>
              </div>

              {/* Red */}
              {(service.privateIp || service.privateDnsName || (service.access === 'PUBLIC_IP' && (service.publicIp || service.publicDnsName || service.publicDnsNameKubiy))) && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Red</h4>
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {service.privateIp && <InfoRow icon={<EnvironmentOutlined />} label="IP Privada" value={<CopyCode text={service.privateIp} title="Copiar IP Privada" />} />}
                    {service.privateDnsName && <InfoRow icon={<EnvironmentOutlined />} label="DNS Privado" value={<CopyCode text={service.privateDnsName} title="Copiar DNS Privado" />} />}
                    {service.access === 'PUBLIC_IP' && service.publicIp && (
                      <InfoRow icon={<EnvironmentOutlined />} label={t('virtualMachines.detail.publicIpDynamic')} value={<CopyCode text={service.publicIp} title={t('virtualMachines.detail.publicIpDynamic')} />} />
                    )}
                    {service.access === 'PUBLIC_IP' && service.publicDnsName && (
                      <InfoRow icon={<EnvironmentOutlined />} label={t('virtualMachines.detail.publicDns')} value={<CopyCode text={service.publicDnsName} title={t('virtualMachines.detail.publicDns')} />} />
                    )}
                    {service.access === 'PUBLIC_IP' && service.publicDnsNameKubiy && (
                      <InfoRow icon={<EnvironmentOutlined />} label={t('virtualMachines.detail.dnsName')} value={<CopyCode text={service.publicDnsNameKubiy} title={t('virtualMachines.detail.dnsName')} />} />
                    )}
                  </div>
                </div>
              )}

              {/* Subscripción */}
              {(() => {
                const sub = (service as any).subscription as {
                  id?: string; status?: string; billingInterval?: string;
                  amount?: number; currency?: string; currentPeriodEnd?: string;
                  cancelAtPeriodEnd?: boolean;
                } | null;
                if (!sub) return null;

                const subStatusConfig: Record<string, { label: string; className: string }> = {
                  ACTIVE:    { label: 'Activa',         className: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
                  PAST_DUE:  { label: 'Pago pendiente', className: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' },
                  CANCELLED: { label: 'Cancelada',      className: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
                  SUSPENDED: { label: 'Suspendida',     className: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
                };
                const sc = subStatusConfig[sub.status || ''] ?? { label: sub.status || '—', className: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' };
                const intervalLabel = sub.billingInterval === 'YEAR' ? 'anual' : 'mensual';
                const amountFmt = sub.amount != null
                  ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: (sub.currency || 'usd').toUpperCase() }).format(sub.amount / 100)
                  : null;
                const nextCharge = sub.currentPeriodEnd
                  ? new Date(sub.currentPeriodEnd).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
                  : null;

                return (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Subscripción</h4>
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                      <InfoRow icon={<TagOutlined />} label="Estado" value={<span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sc.className}`}>{sc.label}</span>} />
                      {amountFmt && <InfoRow icon={<CalendarOutlined />} label="Cobro" value={`${amountFmt} ${intervalLabel}`} />}
                      {nextCharge && <InfoRow icon={<CalendarOutlined />} label={sub.cancelAtPeriodEnd ? 'Cancela el' : 'Próximo cobro'} value={nextCharge} />}
                    </div>
                  </div>
                );
              })()}

              {/* Configuración */}
              {serviceVmConfig && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Configuración</h4>
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    <InfoRow
                      icon={<SettingOutlined />}
                      label={t('virtualMachines.create.step1.backups')}
                      value={
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          serviceVmConfig.backups
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}>
                          {serviceVmConfig.backups ? t('common.yes') : t('common.no')}
                        </span>
                      }
                    />
                    <InfoRow
                      icon={<SettingOutlined />}
                      label={t('virtualMachines.create.step1.monitoring')}
                      value={
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          serviceVmConfig.monitoring !== false
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}>
                          {serviceVmConfig.monitoring !== false ? t('common.yes') : t('common.no')}
                        </span>
                      }
                    />
                  </div>
                </div>
              )}


              {/* Orden */}
              {order && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('virtualMachines.detail.orderId')}</p>
                    <code className="text-xs font-mono text-gray-700 dark:text-gray-300">{order.id}</code>
                  </div>
                  <button
                    onClick={() => navigate(`/orders/${order.id}`)}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    {t('orders.list.order.viewDetails')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="flex gap-1 px-4 sm:px-6 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('events')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'events'
                      ? 'border-black dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t('virtualMachines.detail.events.title')} {isDeploying && <span className="ml-2 text-blue-600 dark:text-blue-400">●</span>}
                </button>
                <button
                  onClick={() => setActiveTab('security')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'security'
                      ? 'border-black dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t('virtualMachines.detail.security')}
                </button>
                <button
                  onClick={() => setActiveTab('documentation')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'documentation'
                      ? 'border-black dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t('virtualMachines.detail.documentation.tab')}
                </button>
                <button
                  onClick={() => setActiveTab('monitoring')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'monitoring'
                      ? 'border-black dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Monitoring
                </button>
                <button
                  onClick={() => setActiveTab('backups')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'backups'
                      ? 'border-black dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Respaldos
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              {activeTab === 'events' && (
                <LazyServiceEventsList serviceId={id || ''} eventsPerPage={10} />
              )}

              {activeTab === 'security' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('virtualMachines.detail.securityRules.title')}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {t('virtualMachines.detail.securityRules.subtitle')}
                    </p>
                  </div>

                  {/* IP Pública */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('virtualMachines.detail.securityRules.publicIp')}
                    </label>
                    {service.access === 'PUBLIC_IP' && service.publicIp ? (
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <code className="text-sm font-mono text-gray-900 dark:text-white">{service.publicIp}</code>
                          <button
                            onClick={() => navigator.clipboard.writeText(service.publicIp!)}
                            className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            title={t('common.copy')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded-r-lg">
                        <div className="flex items-start gap-2">
                          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 text-lg mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">{t('virtualMachines.detail.securityRules.ssmAccessDesc')}</p>
                            <p className="text-sm text-blue-800 dark:text-blue-200">
                              {t('virtualMachines.detail.securityRules.ssmAccessDesc')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {service.access === 'PUBLIC_IP' ? (
                    <>
                      {/* Tabs para Ingress y Egress */}
                      <div className="border-b border-gray-200 dark:border-gray-700">
                        <div className="flex gap-1">
                          <button
                            onClick={() => setSecurityTab('ingress')}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                              securityTab === 'ingress'
                                ? 'border-black dark:border-white text-gray-900 dark:text-white'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                          >
                            Reglas de Entrada (Ingress)
                          </button>
                          <button
                            onClick={() => setSecurityTab('egress')}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                              securityTab === 'egress'
                                ? 'border-black dark:border-white text-gray-900 dark:text-white'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                          >
                            Reglas de Salida (Egress)
                          </button>
                        </div>
                      </div>

                      {/* Contenido de Ingress Tab */}
                      {securityTab === 'ingress' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                              Reglas de Entrada
                            </h4>
                            <button
                              onClick={() => {
                                setNewRule({
                                  direction: 'ingress',
                                  type: 'SSH',
                                  fromPort: 22,
                                  toPort: 22,
                                  protocol: 'tcp',
                                  cidr: '',
                                  description: '',
                                });
                                setEditingRule(null);
                                setShowAddRuleModal(true);
                              }}
                              className="px-4 py-2 text-sm font-medium bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors flex items-center gap-2"
                            >
                              <PlusOutlined />
                              {t('virtualMachines.detail.securityRules.rulesTable.addRule')}
                            </button>
                          </div>
                          {ingressRules.length > 0 ? (
                            <div className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                              <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Tipo</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Puerto</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Protocolo</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">CIDR</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Descripción</th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">{t('virtualMachines.detail.securityRules.rulesTable.actions')}</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                  {ingressRules.map((rule) => (
                                    <tr key={rule.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{rule.type}</td>
                                      <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">
                                        {rule.fromPort === rule.toPort ? rule.fromPort : `${rule.fromPort}-${rule.toPort}`}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white uppercase">{rule.protocol}</td>
                                      <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">{rule.cidr}</td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{rule.description}</td>
                                      <td className="px-4 py-3 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                          <button
                                            onClick={() => handleEditRule(rule)}
                                            className="p-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                            title={t('common.edit')}
                                          >
                                            <SettingOutlined />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteRule(rule.id, 'ingress')}
                                            className="p-1.5 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                            title={t('common.delete')}
                                          >
                                            <DeleteOutlined />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-6 text-center">
                              <p className="text-sm text-gray-500 dark:text-gray-400">{t('virtualMachines.detail.securityRules.rulesTable.noRules')}</p>
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('virtualMachines.detail.securityRules.rulesTable.noRulesDesc')}</p>
                            </div>
                          )}
                          <div className="flex items-center justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button
                              onClick={handleSaveIngressRules}
                              disabled={isSavingIngress}
                              className="px-6 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSavingIngress ? t('virtualMachines.detail.securityRules.saving') : 'Guardar Reglas'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Contenido de Egress Tab */}
                      {securityTab === 'egress' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                              Reglas de Salida
                            </h4>
                            <button
                              onClick={() => {
                                setNewRule({
                                  direction: 'egress',
                                  type: 'SSH',
                                  fromPort: 22,
                                  toPort: 22,
                                  protocol: 'tcp',
                                  cidr: '',
                                  description: '',
                                });
                                setEditingRule(null);
                                setShowAddRuleModal(true);
                              }}
                              className="px-4 py-2 text-sm font-medium bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors flex items-center gap-2"
                            >
                              <PlusOutlined />
                              {t('virtualMachines.detail.securityRules.rulesTable.addRule')}
                            </button>
                          </div>
                          {egressRules.length > 0 ? (
                            <div className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                              <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Tipo</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Puerto</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Protocolo</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">CIDR</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Descripción</th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">{t('virtualMachines.detail.securityRules.rulesTable.actions')}</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                  {egressRules.map((rule) => (
                                    <tr key={rule.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{rule.type}</td>
                                      <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">
                                        {rule.fromPort === rule.toPort ? rule.fromPort : `${rule.fromPort}-${rule.toPort}`}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white uppercase">{rule.protocol}</td>
                                      <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">{rule.cidr}</td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{rule.description}</td>
                                      <td className="px-4 py-3 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                          <button
                                            onClick={() => handleEditRule(rule)}
                                            className="p-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                            title={t('common.edit')}
                                          >
                                            <SettingOutlined />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteRule(rule.id, 'egress')}
                                            className="p-1.5 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                            title={t('common.delete')}
                                          >
                                            <DeleteOutlined />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-6 text-center">
                              <p className="text-sm text-gray-500 dark:text-gray-400">{t('virtualMachines.detail.securityRules.rulesTable.noRules')}</p>
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('virtualMachines.detail.securityRules.rulesTable.noRulesDesc')}</p>
                            </div>
                          )}
                          <div className="flex items-center justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button
                              onClick={handleSaveEgressRules}
                              disabled={isSavingEgress}
                              className="px-6 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSavingEgress ? t('virtualMachines.detail.securityRules.saving') : 'Guardar Reglas'}
                            </button>
                          </div>
                        </div>
                      )}
                    </>) : (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded-r-lg">
                      <div className="flex items-start gap-2">
                        <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 text-lg mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">{t('virtualMachines.detail.connection.secureAccessInfo')}</p>
                          <p className="text-sm text-blue-800 dark:text-blue-200">
                            {t('virtualMachines.detail.securityRules.ssmAccessDesc')}
                          </p>
                        </div>
                      </div>
                    </div>)
                  }
                </div>
              )}

              {activeTab === 'monitoring' && service && (
                <MonitoringTab service={service as any} />
              )}

              {activeTab === 'backups' && (
                <BackupsTab serviceId={id || ''} />
              )}

              {activeTab === 'documentation' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('virtualMachines.detail.documentation.title')}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('virtualMachines.detail.documentation.subtitle')}</p>
                  </div>

                  {isLinux() && (service.publicDnsNameKubiy || service.publicDnsName) && (
                    <div className="space-y-4">
                      <h4 className="text-base font-semibold text-gray-900 dark:text-white">{t('virtualMachines.detail.documentation.ssh.title')}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{t('virtualMachines.detail.documentation.ssh.description')}</p>
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('virtualMachines.detail.documentation.ssh.windowsExample')}</p>
                        <div className="flex items-start gap-2">
                          <code className="flex-1 text-sm font-mono text-gray-900 dark:text-white break-all">
                            ssh -i C:\Users\{t('virtualMachines.detail.documentation.ssh.pathPlaceholder')}\Downloads\{(service.name || id || 'vm').replace(/\s+/g, '')}.pem {getSshUserForOs()}@{service.publicDnsNameKubiy ?? service.publicDnsName ?? ''}
                          </code>
                          <button
                            type="button"
                            onClick={() => navigator.clipboard.writeText(`ssh -i C:\\Users\\${t('virtualMachines.detail.documentation.ssh.pathPlaceholder')}\\Downloads\\${(service.name || id || '').replace(/\s+/g, '')}.pem ${getSshUserForOs()}@${service.publicDnsNameKubiy || service.publicDnsName || ''}`)}
                            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 shrink-0"
                            title={t('common.copy')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{t('virtualMachines.detail.documentation.ssh.replacePath')}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-3">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{t('virtualMachines.detail.documentation.ssh.userByOs')}</p>
                        <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                          <li>Ubuntu → ubuntu</li>
                          <li>Amazon Linux → ec2-user</li>
                          <li>Debian → admin</li>
                          <li>RHEL / Red Hat → ec2-user</li>
                          <li>CentOS → centos</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {isLinux() && !(service.publicDnsNameKubiy || service.publicDnsName) && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('virtualMachines.detail.documentation.ssh.noDns')}</p>
                  )}

                  {!isLinux() && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('virtualMachines.detail.documentation.ssh.windowsOnly')}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Quick Info */}
        <div className="lg:col-span-4 space-y-4 sm:space-y-6">
          {/* Health Status Card */}
          <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('virtualMachines.detail.status')}</h3>
            </div>
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('virtualMachines.detail.status')}</span>
                <ServiceStatusBadge status={service.status} size="sm" showLabel />
              </div>

              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('virtualMachines.detail.health')}</span>
                <div className="flex items-center gap-2">
                  <span
                    className={`w-2.5 h-2.5 rounded-full ${
                      service.health === 'healthy'
                        ? 'bg-green-500'
                        : service.health === 'degraded'
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                  ></span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                    {service.health}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('virtualMachines.detail.lastUpdated')}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {getServiceAge(service.updatedAt)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('virtualMachines.detail.created')}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {getServiceAge(service.createdAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Connection Info Card */}
          {canConnect && (
            <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('virtualMachines.detail.connection.title')}</h3>
              </div>
              <div className="p-6 space-y-3">
                {service.privateIp && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('virtualMachines.detail.privateIp')}</label>
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono block text-gray-900 dark:text-white">
                      {service.privateIp}
                    </code>
                  </div>
                )}
                {service.privateDnsName && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('virtualMachines.detail.privateDns')}</label>
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono block text-gray-900 dark:text-white">
                      {service.privateDnsName}
                    </code>
                  </div>
                )}
                {service.access === 'PUBLIC_IP' && service.publicIp && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('virtualMachines.detail.publicIpDynamic')}</label>
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono block text-gray-900 dark:text-white">
                      {service.publicIp}
                    </code>
                  </div>
                )}
                {service.access === 'PUBLIC_IP' && service.publicDnsName && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('virtualMachines.detail.publicDns')}</label>
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono block text-gray-900 dark:text-white">
                      {service.publicDnsName}
                    </code>
                  </div>
                )}
                {service.access === 'PUBLIC_IP' && service.publicDnsNameKubiy && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('virtualMachines.detail.dnsName')}</label>
                    <div className="flex items-center gap-2">
                      <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white">
                        {service.publicDnsNameKubiy}
                      </code>
                      <button
                        onClick={() => navigator.clipboard.writeText(service.publicDnsNameKubiy!)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        title={t('virtualMachines.detail.dnsName')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                {service.access && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('virtualMachines.detail.accessMethod')}</label>
                    <span className={`text-sm font-medium px-2 py-1 rounded inline-block ${
                      service.access === 'PUBLIC_IP' 
                        ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' 
                        : 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                    }`}>
                      {service.access === 'PUBLIC_IP' ? t('virtualMachines.detail.publicAccess') : t('virtualMachines.detail.ssmAccess')}
                    </span>
                  </div>
                )}
                <div className="space-y-2">
                  {!isWindows() && (
                    <button
                      onClick={handleConnect}
                      className="w-full px-4 py-2 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                    >
                      <CodeOutlined />
                      {t('virtualMachines.detail.connect')}
                    </button>
                  )}
                  
                  {/* Botón para descargar credenciales de Windows */}
                  {isWindows() && (
                    <button
                      onClick={handleDownloadWindowsCredentials}
                      disabled={isDownloadingCredentials || areCredentialsDelivered()}
                      className={`w-full px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                        areCredentialsDelivered()
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isDownloadingCredentials ? (
                        <>
                          <LoadingOutlined spin />
                          {t('virtualMachines.detail.windowsCredentials.downloadingCredentials') || 'Descargando...'}
                        </>
                      ) : areCredentialsDelivered() ? (
                        <>
                          <CheckCircleOutlined />
                          {t('virtualMachines.detail.windowsCredentials.credentialsDownloaded') || 'Credenciales descargadas'}
                        </>
                      ) : (
                        <>
                          <ExportOutlined />
                          {t('virtualMachines.detail.windowsCredentials.downloadWindowsCredentials') || 'Descargar Credenciales de Windows'}
                        </>
                      )}
                    </button>
                  )}

                  {/* Botón para descargar clave SSH (Linux) */}
                  {isLinux() && (
                    <button
                      onClick={handleDownloadLinuxSshKey}
                      disabled={isDownloadingSshKey || isLinuxKeyDelivered()}
                      className={`w-full px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                        isLinuxKeyDelivered()
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isDownloadingSshKey ? (
                        <>
                          <LoadingOutlined spin />
                          {t('virtualMachines.detail.sshKey.downloadingKey') || 'Descargando...'}
                        </>
                      ) : isLinuxKeyDelivered() ? (
                        <>
                          <CheckCircleOutlined />
                          {t('virtualMachines.detail.sshKey.keyDownloaded') || 'Clave descargada'}
                        </>
                      ) : (
                        <>
                          <ExportOutlined />
                          {t('virtualMachines.detail.sshKey.downloadSSHKey') || 'Descargar clave SSH'}
                        </>
                      )}
                    </button>
                  )}
                  
                </div>
              </div>
            </div>
          )}

          {/* Product Info Card */}
          {product && (
            <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('orders.detail.product')}</h3>
              </div>
              <div className="p-6 space-y-3">
                {getOSImage(product, service?.os) && (
                  <div className="flex justify-center mb-4">
                    <div className="w-24 h-24 rounded-lg overflow-hidden flex items-center justify-center">
                      <img 
                        src={getOSImage(product, service?.os)!} 
                        alt={product?.name || product?.os || service?.os || 'OS'} 
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('virtualMachines.detail.operatingSystem')}</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{product.os || product.name || 'N/A'}</p>
                </div>
                {product.description && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Descripción</label>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{product.description}</p>
                  </div>
                )}
                {product.osType && (
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">Tipo de OS</label>
                    <p className="text-sm font-medium text-gray-900 capitalize dark:text-white">{product.osType}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documentación */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Documentación</h3>
            <a
              href="https://docs.kubiy.com/servers"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between px-3 py-2 bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <span>Ver documentación</span>
              <ExportOutlined className="text-xs" />
            </a>
          </div>
        </div>
      </div>
    </div>

      {/* Modal para Agregar/Editar Regla */}

      {showAddRuleModal && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => {
                          setShowAddRuleModal(false);
                          setEditingRule(null);
                          setNewRule({
                            direction: 'ingress',
                            type: 'SSH',
                            fromPort: 22,
                            toPort: 22,
                            protocol: 'tcp',
                            cidr: '',
                            description: '',
                          });
                        }}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div 
              className="bg-white dark:bg-dark-bg rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white dark:bg-dark-bg border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  {editingRule ? 'Editar Regla' : t('virtualMachines.detail.securityRules.rulesTable.addRule')}
                </h3>
                <button
                  onClick={() => {
                    setShowAddRuleModal(false);
                    setEditingRule(null);
                    setNewRule({
                      direction: 'ingress',
                      type: 'SSH',
                      fromPort: 22,
                      toPort: 22,
                      protocol: 'tcp',
                      cidr: '',
                      description: '',
                    });
                  }}
                  className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                >
                  <CloseCircleOutlined className="text-xl" />
                </button>
              </div>
              
              <div className="p-6 space-y-4 dark:bg-dark-bg">
                {/* Dirección */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Dirección *
                  </label>
                  <select
                    value={newRule.direction}
                    onChange={(e) => setNewRule({ ...newRule, direction: e.target.value as 'ingress' | 'egress' })}
                    disabled={!!editingRule}
                    className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none font-mono ${
                      editingRule 
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
                        : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                    }`}
                  >
                    <option value="ingress">Ingress (Entrada)</option>
                    <option value="egress">Egress (Salida)</option>
                  </select>
                </div>

                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('virtualMachines.detail.securityRules.rulesTable.typeLabel')}
                  </label>
                  <select
                    value={newRule.type}
                    onChange={(e) => handleTypeChange(e.target.value as SecurityRule['type'])}
                    disabled={!!editingRule}
                    className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none ${
                      editingRule 
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
                        : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                    }`}
                  >
                    <option value="SSH">SSH</option>
                    <option value="RDP">RDP</option>
                    <option value="MSSQL">MSSQL</option>
                    <option value="MySQL">MySQL</option>
                    <option value="PostgreSQL">PostgreSQL</option>
                    <option value="HTTP">HTTP</option>
                    <option value="HTTPS">HTTPS</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>

                {/* Puertos */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Puerto Desde *
                    </label>
                    <input
                      type="number"
                      value={newRule.fromPort}
                      onChange={(e) => setNewRule({ ...newRule, fromPort: parseInt(e.target.value) || 0 })}
                      min="1"
                      max="65535"
                      disabled={!!editingRule}
                      className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none font-mono ${
                        editingRule 
                          ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
                          : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Puerto Hasta *
                    </label>
                    <input
                      type="number"
                      value={newRule.toPort}
                      onChange={(e) => setNewRule({ ...newRule, toPort: parseInt(e.target.value) || 0 })}
                      min="1"
                      max="65535"
                      disabled={!!editingRule}
                      className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none font-mono ${
                        editingRule 
                          ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
                          : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                      }`}
                    />
                  </div>
                </div>

                {/* Protocolo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('virtualMachines.detail.securityRules.rulesTable.protocolLabel')}
                  </label>
                  <select
                    value={newRule.protocol}
                    onChange={(e) => setNewRule({ ...newRule, protocol: e.target.value as 'tcp' | 'udp' | 'icmp' })}
                    disabled={!!editingRule}
                    className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none ${
                      editingRule 
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
                        : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                    }`}
                  >
                    <option value="tcp">TCP</option>
                    <option value="udp">UDP</option>
                    <option value="icmp">ICMP</option>
                  </select>
                </div>

                {/* CIDR */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('virtualMachines.detail.securityRules.rulesTable.sourceLabel')} (CIDR) *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newRule.cidr}
                      onChange={(e) => setNewRule({ ...newRule, cidr: e.target.value })}
                      placeholder="0.0.0.0/0 o 192.168.1.0/24"
                      disabled={!!editingRule}
                      className={`flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none font-mono ${
                        editingRule 
                          ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
                          : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={handleAddMyIP}
                      disabled={!!editingRule}
                      className={`px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg transition-colors ${
                        editingRule
                          ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                          : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t('virtualMachines.detail.securityRules.rulesTable.addMyIP')}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Formato CIDR: 0.0.0.0/0 para permitir todo, o 192.168.1.0/24 para una red específica
                  </p>
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('virtualMachines.detail.securityRules.rulesTable.nameLabel')} *
                  </label>
                  <input
                    type="text"
                    value={newRule.description}
                    onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                    placeholder={t('virtualMachines.detail.securityRules.rulesTable.namePlaceholder')}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('virtualMachines.detail.securityRules.rulesTable.nameHint')}
                  </p>
                </div>

                {/* Botones */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setShowAddRuleModal(false);
                      setEditingRule(null);
                      setNewRule({
                        direction: 'ingress',
                        type: 'SSH',
                        fromPort: 22,
                        toPort: 22,
                        protocol: 'tcp',
                        cidr: '',
                        description: '',
                      });
                    }}
                    className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={editingRule ? handleUpdateRule : handleAddRule}
                    disabled={
                      editingRule 
                        ? !newRule.description.trim() 
                        : !newRule.cidr.trim() || !newRule.description.trim() || !newRule.fromPort || !newRule.toPort
                    }
                    className="px-6 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-dark-bg dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editingRule ? t('common.save') : t('virtualMachines.detail.securityRules.rulesTable.addRuleButton')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t('virtualMachines.detail.deleteWarning')}
        message={`${t('virtualMachines.detail.deleteWarningMessage')} "${service.name}"?\n\n${t('virtualMachines.detail.deleteWarningDetails')}`}
        confirmText={t('common.delete')}
        variant="danger"
        requireInput
        inputPlaceholder={t('virtualMachines.detail.deleteConfirmText')}
        expectedInput={service.name}
        loading={isProcessing}
      />

      </>);
};

export default VMDetail;

